import React from 'react';
import { usePedidos, useStatusPedidos, useAlterarStatusPedido } from '../../hooks/useStatusPedidos';
import { usePageTitle } from '../../hooks/usePageTitle';

const Pedidos: React.FC = () => {
  usePageTitle('Pedidos');
  const { data: pedidos, isLoading: loadingPedidos } = usePedidos();
  const { data: statusPedidos, isLoading: loadingStatus } = useStatusPedidos();
  const { mutate: alterarStatus, isPending } = useAlterarStatusPedido();

  const formatCurrency = (value: number) => {
    return new Intl.NumberFormat('pt-BR', {
      style: 'currency',
      currency: 'BRL',
    }).format(value);
  };

  const formatDateTime = (dateString: string) => {
    const date = new Date(dateString);
    return new Intl.DateTimeFormat('pt-BR', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    }).format(date);
  };

  const getStatusBadgeClass = (statusDescricao: string) => {
    const status = statusDescricao.toLowerCase();

    if (status.includes('aguardando') || status.includes('pendente')) {
      return 'bg-yellow-100 text-yellow-800';
    }
    if (status.includes('pago') || status.includes('concluido') || status.includes('conclu')) {
      return 'bg-green-100 text-green-800';
    }
    if (status.includes('cancelado')) {
      return 'bg-red-100 text-red-800';
    }
    if (status.includes('estornado')) {
      return 'bg-gray-100 text-gray-800';
    }
    return 'bg-blue-100 text-blue-800';
  };

  const handleAlterarStatus = (pedidoId: number, novoStatusId: number) => {
    alterarStatus({ pedidoId, novoStatusId });
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <h1 className="text-3xl font-bold text-gray-800">Pedidos</h1>
      </div>

      {loadingPedidos || loadingStatus ? (
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-gray-500 text-center py-8">Carregando pedidos...</p>
        </div>
      ) : pedidos && pedidos.length > 0 ? (
        <div className="bg-white rounded-lg shadow overflow-hidden">
          <table className="min-w-full divide-y divide-gray-200">
            <thead className="bg-gray-50">
              <tr>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  ID
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Data/Hora
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Cliente
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Valor Total
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Status
                </th>
                <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                  Alterar Status
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y divide-gray-200">
              {pedidos.map((pedido) => (
                <tr key={pedido.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-medium text-gray-900">#{pedido.id}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{formatDateTime(pedido.dataHora)}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {pedido.numeroMesa ? `Mesa ${pedido.numeroMesa}` : '-'}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm font-semibold text-gray-900">
                      {formatCurrency(pedido.valorTotal)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`px-3 py-1 inline-flex text-xs leading-5 font-semibold rounded-full ${getStatusBadgeClass(
                        pedido.statusDescricao
                      )}`}
                    >
                      {pedido.statusDescricao}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <select
                      value={pedido.statusPedidoId}
                      onChange={(e) =>
                        handleAlterarStatus(pedido.id, parseInt(e.target.value))
                      }
                      disabled={isPending}
                      className="px-3 py-2 border rounded-lg focus:outline-none focus:ring-2 focus:ring-blue-500 text-sm disabled:bg-gray-100 disabled:cursor-not-allowed"
                    >
                      {statusPedidos?.map((status) => (
                        <option key={status.id} value={status.id}>
                          {status.descricao}
                        </option>
                      ))}
                    </select>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      ) : (
        <div className="bg-white rounded-lg shadow p-6">
          <p className="text-gray-500 text-center py-8">Nenhum pedido encontrado</p>
        </div>
      )}
    </div>
  );
};

export default Pedidos;
